import { Link as RouterLink } from "react-router-dom";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import StorefrontLayout from "components/storefront/StorefrontLayout";
import useStorefrontCopy from "hooks/useStorefrontCopy";

export default function DeleteAccount() {
  const { t } = useStorefrontCopy();
  const steps = [
    t(
      "legal.appInstruction",
      "Sign in to your customer or distributor account."
    ),
    "Go to Profile, then choose Delete account.",
    "Confirm the request. We will send a confirmation email and review the account for deletion.",
  ];
  const retainedRecords = [
    "Orders, invoices, payments, credits, refunds, shipping labels, and accounting records.",
    t(
      "legal.retainedRecords",
      "Order, return, warranty, support, and fraud-prevention records."
    ),
    "Records we must keep for tax, legal, security, dispute, or operational requirements.",
  ];
  return (
    <StorefrontLayout maxWidth="lg">
      <MDBox py={{ xs: 3, md: 6 }}>
        <MDBox mb={4}>
          <MDTypography
            variant="overline"
            fontWeight="bold"
            sx={{ color: "#D7261E", letterSpacing: "0.08em" }}
          >
            {t("common.brandFallback", "Storefront")}
          </MDTypography>
          <MDTypography
            variant="h2"
            fontWeight="bold"
            mt={1}
            mb={1.5}
            sx={{ color: "#111111" }}
          >
            {t("legal.deleteTitle", "Delete Your Account")}
          </MDTypography>
          <MDTypography
            variant="body1"
            sx={{ color: "#555555", maxWidth: 820, lineHeight: 1.8 }}
          >
            {t(
              "legal.deleteIntro",
              "You can request deletion of your customer or distributor account from the account portal."
            )}
          </MDTypography>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 14px 36px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={1.25}
                  sx={{ color: "#111111" }}
                >
                  How to request deletion
                </MDTypography>
                <MDBox component="ol" sx={{ pl: 3, mt: 0, mb: 3 }}>
                  {steps.map((step) => (
                    <MDBox component="li" key={step} mb={1}>
                      <MDTypography
                        variant="body2"
                        sx={{ color: "#4A4A4A", lineHeight: 1.75 }}
                      >
                        {step}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>

                <Divider sx={{ my: 3 }} />

                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={1.25}
                  sx={{ color: "#111111" }}
                >
                  What happens next
                </MDTypography>
                <MDTypography
                  variant="body2"
                  mb={2}
                  sx={{ color: "#4A4A4A", lineHeight: 1.75 }}
                >
                  {t(
                    "legal.verificationText",
                    "We will verify the request and may contact you from an official company email address if more verification is needed."
                  )}
                </MDTypography>

                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={1.25}
                  sx={{ color: "#111111" }}
                >
                  Records we may retain
                </MDTypography>
                <MDBox component="ul" sx={{ pl: 3, mt: 0 }}>
                  {retainedRecords.map((item) => (
                    <MDBox component="li" key={item} mb={1}>
                      <MDTypography
                        variant="body2"
                        sx={{ color: "#4A4A4A", lineHeight: 1.75 }}
                      >
                        {item}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 3,
                position: { md: "sticky" },
                top: { md: 140 },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={1}
                  sx={{ color: "#111111" }}
                >
                  Need help?
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{ color: "#555555", lineHeight: 1.7 }}
                >
                  If you cannot access the app, email us from the email address
                  on your account:
                </MDTypography>
                <MDBox mt={2}>
                  <Link
                    href="mailto:sales@vbspecialties.com?subject=Account%20Deletion%20Request"
                    underline="hover"
                    sx={{ fontWeight: 700 }}
                  >
                    sales@vbspecialties.com
                  </Link>
                </MDBox>
                <Divider sx={{ my: 3 }} />
                <MDBox display="flex" flexDirection="column" gap={1}>
                  <MDButton
                    component={RouterLink}
                    to="/privacy"
                    variant="outlined"
                    color="dark"
                    fullWidth
                  >
                    Privacy policy
                  </MDButton>
                  <MDButton
                    component={RouterLink}
                    to="/"
                    variant="contained"
                    color="info"
                    fullWidth
                  >
                    Back to home
                  </MDButton>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </StorefrontLayout>
  );
}
